import * as Sentry from '@sentry/browser';

declare global {
    interface Window {
        Sentry?: {
            dsn: string;
            environment: string;
            release?: string;
        };
    }
}

// Noms des fonctions/variables Alpine.js (handlers @scroll.window, méthodes de
// composants x-data, state) qui déclenchent une ReferenceError en rafale quand
// la page est utilisée avant qu'Alpine/ESI aient fini de charger. À compléter
// si de nouveaux handlers du même genre apparaissent dans les logs Sentry.
const NOT_LOADED_ERROR_NAMES = [
    'expressAppointementToQuotation',
    'handleSticky',
    'checkScrolling',
    'isOpen',
    'active',
    'activeIndex',
    'closeChoices',
    'trybaPhoneMask',
    'storeDatas',
    'pdvStoredName',
    'initAbTestReviewSlider',
    'handleStickyTopMenu',
    'initStepWording',
    'start',
    'errorMessages',
    'buttonDisable',
    'firstOpening',
    'nbOfChars',
    'closeMenuDesktop',
];
const notLoadedErrorPattern = new RegExp(`^(${NOT_LOADED_ERROR_NAMES.join('|')}) is not defined$`);

// Ces ReferenceError ne sont un symptôme de « page pas chargée » que tant qu'Alpine n'a pas démarré
// (Alpine.start() est appelé sur DOMContentLoaded). Une fois `alpine:initialized` émis, la même erreur
// est un vrai bug de template (portée x-data) : elle doit remonter normalement dans Sentry.
let alpineInitialized = false;
document.addEventListener('alpine:initialized', () => {
    alpineInitialized = true;
}, { once: true });

if(window.Sentry?.dsn){
    // Réinitialisé à chaque chargement de page : on veut un seul événement
    // "page pas chargée" par page vue, pas par session.
    let pageNotLoadedErrorSent = false;

    Sentry.init({
        dsn: window.Sentry.dsn,
        environment: window.Sentry.environment,
        release: window.Sentry.release ?? undefined,
        tracesSampleRate: 1.0,
        integrations: [
            Sentry.browserTracingIntegration(),
        ],
        allowUrls: [
            /https?:\/\/((cdn|www)\.)?tryba\.com/,
            /https?:\/\/((rct-01|rct-02|www)\.)?dev-tryba\.fr/,
        ],
        ignoreErrors: [
            // Le widget eKonsilio rejette {status, statusText} sans .catch() quand
            // ses requêtes sont bloquées (adblock, DuckDuckGo) ; sans stack trace,
            // allowUrls ne peut pas filtrer ces rejets.
            'Object captured as promise rejection with keys: status, statusText',
            // play() rejeté parce qu'un pause() (ou un rechargement du média) l'a
            // interrompu : bénin, documenté par Chrome (goo.gl/LdLk22). Nos appels
            // ont un .catch() ; ce qui reste vient de scripts tiers (déblocage audio
            // play()/pause() du widget de chat), sans stack trace, donc hors de
            // portée d'allowUrls.
            /^AbortError: The play\(\) request was interrupted/,
        ],
        beforeSend(event, hint) {
            const error = hint?.originalException;
            const message = event.exception?.values?.[0]?.value ?? '';
            const isNotLoadedError = !alpineInitialized && error instanceof ReferenceError && notLoadedErrorPattern.test(message);

            if(isNotLoadedError){
                // Une seule occurrence envoyée par page vue : les suivantes sont
                // le symptôme du même incident (page pas encore chargée), pas
                // des erreurs distinctes à tracer une par une.
                if(pageNotLoadedErrorSent){
                    return null;
                }
                pageNotLoadedErrorSent = true;
                event.fingerprint = ['page-not-loaded'];
                if(event.exception?.values?.[0]){
                    event.exception.values[0].value = `Page not fully loaded: ${message}`;
                }
            }

            return event;
        },
    });
}

export default Sentry;
